/**
 * Hero visual: a phone lock-screen receiving notifications from
 * different channels, with a single AI-agent card on the left fading
 * through Claude / Codex / Cursor / Hermes — the "voice" the homepage
 * headline is talking about.
 *
 * The agent ↔ phone link is drawn as TWO curves with dots flowing in
 * opposite directions (AI → you, you → AI) so the two-way story reads
 * at a glance, and a couple of the notification cards carry an
 * attachment chip (paperclip + filename) to show files ride along.
 *
 * Brand glyphs are inlined simple-icons SVG paths (24×24 viewbox)
 * rather than fetched from a CDN so the visual works offline and
 * survives any CSP tightening down the line.
 */

import { AGENTS } from './agents';
import { CHANNELS, type LandingChannelDescriptor } from './channels';

interface Notif {
  readonly channel: LandingChannelDescriptor;
  readonly when: string;
  readonly sender: string;
  readonly body: string;
  /** Optional attachment filename — renders a paperclip chip on the card. */
  readonly attachment?: string;
}

const NOTIFS: Notif[] = [
  notif('telegram', 'now', 'Vocal Cord', 'Build #4827 finished — all tests passing ✓', 'coverage.svg'),
  notif('slack', '1m ago', 'Vocal Cord', '@matus PR #341 ready for review whenever you have a sec'),
  notif('email', '4m ago', 'Vocal Cord <ai@vocalcord.io>', 'Re: production deploy — rolled out, healthy.', 'deploy-log.txt'),
  notif('discord', '12m ago', 'Vocal Cord', 'Long-running task done. Want me to summarise the diff?'),
  notif('line', '23m ago', 'Vocal Cord', 'Daily summary: 14 commits, 3 PRs merged, 0 alerts.'),
];

function notif(
  kind: string,
  when: string,
  sender: string,
  body: string,
  attachment?: string,
): Notif {
  const channel = CHANNELS.find((c) => c.kind === kind);
  if (!channel) throw new Error(`unknown channel kind in notif: ${kind}`);
  return { channel, when, sender, body, attachment };
}

/** Lucide "paperclip" path (24×24), stroked — used for attachment chips. */
const PAPERCLIP_PATH =
  'M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48';

// SVG layout — landscape canvas with the phone centred.
const VB_W = 1200;
const VB_H = 720;

const PHONE_W = 380;
const PHONE_H = 660;
const PHONE_X = (VB_W - PHONE_W) / 2;
const PHONE_Y = (VB_H - PHONE_H) / 2;

const STATUS_H = 28;
const HEADER_H = 70;
const CARD_GAP = 10;
const CARD_W = PHONE_W - 32;
const CARD_H = 72;
const CARD_X = PHONE_X + 16;
const CARD_Y_START = PHONE_Y + STATUS_H + HEADER_H + 14;

// Logo tile sizing — the simple-icons paths use a 24×24 viewbox, so we
// scale them down inside a 48×48 brand tile to leave breathing room
// (Telegram's circle was previously full-bleed, hence the apparent
// overflow into the rounded corners of the tile).
const TILE_SIZE = 48;
const LOGO_INSET = 10;
const LOGO_SCALE = (TILE_SIZE - LOGO_INSET * 2) / 24;

// Single agent card on the left of the phone — fades through these.
const AGENT_CARD_X = 80;
const AGENT_CARD_Y = PHONE_Y + 220;
const AGENT_CARD_W = 220;
const AGENT_CARD_H = 80;

export function HeroVisual() {
  return (
    <div className="hero-visual" aria-hidden="true">
      {/* Cropped viewBox: content spans x≈80 (agent card) to x≈790
          (phone right edge); cropping the empty margins lets the visual
          fill a half-width hero column instead of floating in a wide
          landscape canvas. */}
      <svg
        viewBox={`40 0 800 ${VB_H}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="phone-bezel" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" className="hv-stop-accent" />
            <stop offset="100%" className="hv-stop-accent2" />
          </linearGradient>
          <radialGradient id="hero-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" className="hv-stop-accent" stopOpacity="0.25" />
            <stop offset="100%" className="hv-stop-accent" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ambient glow behind the phone */}
        <ellipse
          cx={VB_W / 2}
          cy={VB_H / 2}
          rx={PHONE_W * 0.85}
          ry={PHONE_H * 0.7}
          fill="url(#hero-glow)"
        />

        {/* left: rotating agent card */}
        <g className="hv-side hv-side-left">
          <text
            className="hv-muted"
            x={AGENT_CARD_X + 40}
            y={PHONE_Y + 30}
            fontSize="13"
            fontWeight="600"
            letterSpacing="2"
          >
            YOUR AI
          </text>
          {AGENTS.map((agent, i) => (
            <g
              key={agent.slug}
              className="hv-agent-cycle"
              style={{ animationDelay: `${i * 2}s` }}
            >
              <rect
                className="hv-bg-elev hv-stroke-border"
                x={AGENT_CARD_X}
                y={AGENT_CARD_Y}
                width={AGENT_CARD_W}
                height={AGENT_CARD_H}
                rx={14}
                strokeWidth="1.5"
              />
              <rect
                x={AGENT_CARD_X + 14}
                y={AGENT_CARD_Y + 16}
                width={48}
                height={48}
                rx={11}
                fill={agent.brandColor}
              />
              {agent.logoPath ? (
                <g
                  transform={`translate(${AGENT_CARD_X + 14 + LOGO_INSET} ${
                    AGENT_CARD_Y + 16 + LOGO_INSET
                  }) scale(${LOGO_SCALE})`}
                >
                  <path d={agent.logoPath} fill="#fff" />
                </g>
              ) : (
                <text
                  x={AGENT_CARD_X + 14 + 24}
                  y={AGENT_CARD_Y + 16 + 32}
                  textAnchor="middle"
                  fontSize="22"
                  fontWeight="700"
                  fill="#fff"
                >
                  {agent.letterMark}
                </text>
              )}
              <text
                className="hv-fg"
                x={AGENT_CARD_X + 78}
                y={AGENT_CARD_Y + 38}
                fontSize="16"
                fontWeight="700"
              >
                {agent.displayName}
              </text>
              <text
                className="hv-muted"
                x={AGENT_CARD_X + 78}
                y={AGENT_CARD_Y + 58}
                fontSize="12"
              >
                sending you a message…
              </text>
            </g>
          ))}
        </g>

        {/* two-way connection rails between the agent card and the phone.
            TOP rail carries a dot agent → phone ("AI → you"); BOTTOM rail
            carries a dot phone → agent ("you → AI"). */}
        {(() => {
          const cardR = AGENT_CARD_X + AGENT_CARD_W; // 300
          const railMid = (cardR + PHONE_X) / 2; // 355
          // Drawn left→right; dot flows agent → phone.
          const topRail = `M ${cardR} 296 C ${railMid} 300, ${railMid + 10} 336, ${PHONE_X} 340`;
          // Drawn phone→agent; dot flows phone → agent.
          const botRail = `M ${PHONE_X} 376 C ${railMid + 10} 380, ${railMid} 316, ${cardR} 312`;
          return (
            <>
              <path className="hv-stroke-border" d={topRail} fill="none" strokeWidth="1.5" />
              <path className="hv-stroke-border" d={botRail} fill="none" strokeWidth="1.5" />
              <circle r="5" className="hv-fill-accent">
                <animateMotion dur="2.4s" repeatCount="indefinite" path={topRail} />
              </circle>
              <circle r="4" className="hv-fill-accent" opacity="0.7">
                <animateMotion dur="2.8s" repeatCount="indefinite" path={botRail} />
              </circle>
              <text className="hv-muted" x={railMid} y="294" fontSize="10" fontWeight="600" textAnchor="middle">
                AI → you
              </text>
              <text className="hv-muted" x={railMid} y="404" fontSize="10" fontWeight="600" textAnchor="middle">
                you → AI
              </text>
            </>
          );
        })()}

        {/* phone bezel + body */}
        <rect
          x={PHONE_X - 4}
          y={PHONE_Y - 4}
          width={PHONE_W + 8}
          height={PHONE_H + 8}
          rx={42}
          fill="url(#phone-bezel)"
          opacity="0.85"
        />
        <rect
          className="hv-bg"
          x={PHONE_X}
          y={PHONE_Y}
          width={PHONE_W}
          height={PHONE_H}
          rx={38}
        />

        {/* status bar */}
        <text
          className="hv-fg"
          x={PHONE_X + 32}
          y={PHONE_Y + 22}
          fontSize="13"
          fontWeight="700"
        >
          9:41
        </text>
        {/* signal dots */}
        <g transform={`translate(${PHONE_X + PHONE_W - 76} ${PHONE_Y + 14})`}>
          <circle className="hv-fg" cx={4} cy={6} r="2" />
          <circle className="hv-fg" cx={12} cy={6} r="2" />
          <circle className="hv-fg" cx={20} cy={6} r="2" />
          <circle className="hv-muted" cx={28} cy={6} r="2" />
          {/* wifi */}
          <path
            className="hv-fg"
            d="M 38 8 a 6 6 0 0 1 10 0 m -8 2 a 4 4 0 0 1 6 0 m -4 2 a 1.5 1.5 0 0 1 2 0"
            fill="none"
            strokeWidth="1.5"
            stroke="currentColor"
          />
          {/* battery */}
          <rect
            x={56}
            y={2}
            width={16}
            height={9}
            rx={2}
            fill="none"
            className="hv-stroke-accent"
            strokeWidth="1.2"
          />
          <rect className="hv-fill-accent" x={58} y={4} width={10} height={5} />
        </g>

        {/* lock-screen header */}
        <text
          className="hv-muted"
          x={PHONE_X + PHONE_W / 2}
          y={PHONE_Y + STATUS_H + 30}
          textAnchor="middle"
          fontSize="13"
          fontWeight="600"
        >
          Tuesday, May 6
        </text>
        <text
          className="hv-fg"
          x={PHONE_X + PHONE_W / 2}
          y={PHONE_Y + STATUS_H + 64}
          textAnchor="middle"
          fontSize="34"
          fontWeight="700"
        >
          9:41
        </text>

        {/* notification stack */}
        {NOTIFS.map((n, i) => {
          const y = CARD_Y_START + i * (CARD_H + CARD_GAP);
          return (
            <g
              key={n.channel.kind}
              className="hv-notif"
              style={{ animationDelay: `${i * 0.6}s` }}
            >
              <rect
                className="hv-bg-elev"
                x={CARD_X}
                y={y}
                width={CARD_W}
                height={CARD_H}
                rx={14}
                opacity="0.96"
              />
              {/* logo tile */}
              <rect
                x={CARD_X + 12}
                y={y + 12}
                width={TILE_SIZE}
                height={TILE_SIZE}
                rx={11}
                fill={n.channel.brandColor}
              />
              {n.channel.logoPath && (
                <g
                  transform={`translate(${CARD_X + 12 + LOGO_INSET} ${
                    y + 12 + LOGO_INSET
                  }) scale(${LOGO_SCALE})`}
                >
                  <path d={n.channel.logoPath} fill="#fff" />
                </g>
              )}
              {/* sender + when */}
              <text
                className="hv-fg"
                x={CARD_X + 72}
                y={y + 26}
                fontSize="13"
                fontWeight="700"
              >
                {n.sender}
              </text>
              <text
                className="hv-muted"
                x={CARD_X + CARD_W - 12}
                y={y + 26}
                fontSize="11"
                textAnchor="end"
              >
                {n.when}
              </text>
              {/* body */}
              <text
                className="hv-fg"
                x={CARD_X + 72}
                y={y + 48}
                fontSize="12"
                opacity="0.85"
              >
                {truncate(n.body, n.attachment ? 24 : 36)}
              </text>
              <text
                className="hv-muted"
                x={CARD_X + 72}
                y={y + 62}
                fontSize="11"
              >
                {n.channel.displayName}
              </text>
              {/* attachment chip, bottom-right */}
              {n.attachment &&
                (() => {
                  const pillW = 26 + n.attachment.length * 5.9;
                  const pillX = CARD_X + CARD_W - 12 - pillW;
                  const pillY = y + 44;
                  return (
                    <g>
                      <rect
                        className="hv-bg hv-stroke-border"
                        x={pillX}
                        y={pillY}
                        width={pillW}
                        height={18}
                        rx={9}
                        strokeWidth="1"
                      />
                      <g
                        transform={`translate(${pillX + 7} ${pillY + 4}) scale(0.42)`}
                      >
                        <path
                          d={PAPERCLIP_PATH}
                          fill="none"
                          className="hv-stroke-accent"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <text
                        className="hv-fill-accent"
                        x={pillX + 20}
                        y={pillY + 13}
                        fontSize="10"
                        fontWeight="600"
                      >
                        {n.attachment}
                      </text>
                    </g>
                  );
                })()}
            </g>
          );
        })}

        {/* notch */}
        <rect
          className="hv-bg"
          x={PHONE_X + PHONE_W / 2 - 60}
          y={PHONE_Y - 1}
          width={120}
          height={28}
          rx={14}
        />
      </svg>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
